import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { legacyApi, PurchaseOrder } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Download, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { StandardActions } from '../components/StandardActions';
import { getPurchaseOrderStatusMeta } from '../utils/statuses';
import { useAuth } from '../../../context/AuthContext';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';

export const LegacyPurchaseOrders: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { hasPermission } = useAuth();
    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        status: 'all'
    });

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadOrders = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : orders.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        try {
            const response = await legacyApi.purchasing.listOrdersPaged({
                q: debouncedQ || undefined,
                status_id: state.status !== 'all' ? Number(state.status) : undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setOrders((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadOrders(true);
    }, [debouncedQ, state.status]);

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Purchase Orders...</div>;

    const columns = [
        { header: "PO ID", accessor: (p: PurchaseOrder) => p.po_number || `HC/PO/${String(p.po_id).padStart(4, '0')}`, className: 'font-mono whitespace-nowrap w-32' },
        { header: "Supplier", accessor: (p: PurchaseOrder) => p.supplier_name || '-', className: 'font-medium min-w-[200px] max-w-[300px] truncate' },
        { header: "PO Date", accessor: (p: PurchaseOrder) => p.po_date ? new Date(p.po_date).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        {
            header: "Status",
            accessor: (p: PurchaseOrder) => {
                const statusMeta = getPurchaseOrderStatusMeta(p.po_status_id || 1, p.status_name || '');
                return (
                    <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-36 text-center ${statusMeta.badgeClass}`}>
                        {statusMeta.label}
                    </span>
                );
            },
            className: 'w-36 text-center'
        },
        { header: "PO Amount", accessor: (p: PurchaseOrder) => p.po_amount ? p.po_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', className: 'w-28 text-right font-medium tabular-nums' },
        {
            header: "Actions",
            accessor: (p: PurchaseOrder) => (
                    <StandardActions
                    view={hasPermission('purchasing.po.view') ? {
                        title: "View Details",
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/purchase-orders/${p.po_id}`);
                        },
                        icon: <Eye className="w-4 h-4 text-green-600" />
                    } : undefined}
                    edit={hasPermission('purchasing.po.edit') ? {
                        title: "Edit",
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/purchase-orders/edit/${p.po_id}`);
                        },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />
                    } : undefined}
                    download={hasPermission('purchasing.po.pdf') ? {
                        title: "Download PDF",
                        onClick: (e) => {
                            e.stopPropagation();
                            legacyApi.purchasing.downloadPDF(p.po_id);
                        },
                        icon: <Download className="w-4 h-4 text-purple-600" />
                    } : undefined}
                    remove={hasPermission('purchasing.po.delete') ? {
                        title: "Delete",
                        onClick: async (e) => {
                            e.stopPropagation();
                            const ok = await confirm('Delete this purchase order?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
                            if (!ok) return;
                            try {
                                await legacyApi.purchasing.deletePurchaseOrder(p.po_id);
                                setOrders(prev => prev.filter(o => o.po_id !== p.po_id));
                                setTotalCount(prev => Math.max(prev - 1, 0));
                                toast.success('Purchase order deleted');
                            } catch (err) {
                                console.error(err);
                                toast.error('Failed to delete purchase order');
                            }
                        },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />
                    } : undefined}
                />
            ),
            className: "text-right"
        }
    ];

    return (
        <div className="h-full flex flex-col space-y-6">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">Purchase Orders</h2>
                    <p className="text-muted-foreground">Manage simplified purchase orders</p>
                </div>
                <Button onClick={() => navigate('/legacy/purchase-orders/new')} disabled={!hasPermission('purchasing.po.create')}>
                    <Plus className="h-4 w-4 mr-2" /> New PO
                </Button>
            </div>
            <FlowEntryBanner
                title="Purchasing Flow: PO -> GRN -> Supplier Bill"
                pathLabel="PO to Supplier Bill"
                nextHint="Approve PO, receive GRN, then post/pay supplier bill."
                helpModule="purchasing"
            />
            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search PO, supplier, quotation..."
                controls={[
                    {
                        key: 'status',
                        label: 'Status',
                        type: 'select',
                        value: state.status,
                        onChange: (value) => setFilter('status', value),
                        options: [
                            { value: 'all', label: 'All Statuses' },
                            { value: '1', label: 'Draft' },
                            { value: '2', label: 'Approved' },
                            { value: '3', label: 'Partially Received' },
                            { value: '4', label: 'Received' },
                            { value: '5', label: 'Billed' },
                            { value: '6', label: 'Closed' },
                            { value: '7', label: 'Cancelled' }
                        ]
                    }
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={`${orders.length} of ${totalCount}`}
            />
            <div className="flex-1 overflow-auto rounded-md border">
                <>
                    <CompactTable
                        data={orders}
                        columns={columns}
                        keyField="po_id"
                        loading={loading}
                        onRowClick={hasPermission('purchasing.po.view') ? (row) => navigate(`/legacy/purchase-orders/${row.po_id}`) : undefined}
                    />
                    {hasMore && (
                        <div className="flex justify-center mt-4">
                            <Button variant="ghost" onClick={() => loadOrders(false)} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load older purchase orders'}
                            </Button>
                        </div>
                    )}
                </>
            </div>
        </div>
    );
};
